import { useState } from "react";
import { api, useGet } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmt } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BellRing, Trash2, ArrowUp, ArrowDown } from "lucide-react";

const when = (iso) => new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export function PriceAlerts({ refreshKey }) {
  const { user } = useAuth();
  const { data: d, reload } = useGet(user ? "/price-alerts" : null, refreshKey);
  const [level, setLevel] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!level) return;
    setBusy(true);
    try {
      const r = await api.post("/price-alerts", { level: Number(level), note });
      toast.success(`Alert armed: gold ${r.data.direction} $${fmt(r.data.level)}`);
      setLevel(""); setNote(""); reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add alert");
    } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/price-alerts/${id}`); reload(); } catch { toast.error("Could not remove alert"); }
  };

  return (
    <div data-testid="price-alerts-card" className="rounded-2xl border border-white/10 bg-[#0B0F17]/80 p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-1">
        <BellRing size={16} className="text-[#F472B6]" />
        <h3 className="text-base md:text-lg font-semibold text-white">Price alerts</h3>
        {d?.spot && <span data-testid="price-alerts-spot" className="ml-auto text-[11px] font-mono-num text-[#9CA3AF]">spot ${fmt(d.spot)}</span>}
      </div>
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num mb-3">Bell + push the moment live spot touches your level · checked every minute</p>
      {!user ? (
        <div data-testid="price-alerts-signin" className="text-xs text-[#9CA3AF]">Sign in to set price alerts — they're tied to your account and pushed to your phone.</div>
      ) : (
        <>
          <form onSubmit={add} className="flex flex-col sm:flex-row gap-2">
            <Input data-testid="price-alert-level-input" type="number" step="0.01" min="500" max="20000" placeholder="e.g. 4050" value={level}
              onChange={(e) => setLevel(e.target.value)} className="h-9 bg-white/5 border-white/10 font-mono-num sm:w-36" required />
            <Input data-testid="price-alert-note-input" placeholder="Note (optional)" value={note} maxLength={80} onChange={(e) => setNote(e.target.value)} className="h-9 bg-white/5 border-white/10 flex-1" />
            <Button data-testid="price-alert-add-button" type="submit" disabled={busy || !level} className="h-9 bg-[#F472B6] hover:bg-[#EC4899] text-black font-semibold">Add alert</Button>
          </form>
          <ul data-testid="price-alerts-list" className="mt-3 flex flex-col gap-1.5">
            {d && d.alerts.length === 0 && <li className="text-xs text-[#6B7280]">No alerts yet. Add a level above or below the current spot.</li>}
            {d?.alerts.map((a) => (
              <li key={a.id} data-testid={`price-alert-${a.id}`} className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2 text-xs">
                {a.direction === "above" ? <ArrowUp size={12} className="text-[#10B981]" /> : <ArrowDown size={12} className="text-[#EF4444]" />}
                <span className="font-mono-num text-white">${fmt(a.level)}</span>
                <span className="text-[#6B7280]">{a.direction}</span>
                {a.note && <span className="text-[#9CA3AF] truncate">· {a.note}</span>}
                <span className={`ml-auto font-mono-num ${a.status === "hit" ? "text-[#F472B6]" : "text-[#9CA3AF]"}`}>
                  {a.status === "hit" ? `hit ${when(a.triggered_at)} @ $${fmt(a.triggered_price)}` : "armed"}
                </span>
                <button data-testid={`price-alert-delete-${a.id}`} onClick={() => remove(a.id)} className="text-[#6B7280] hover:text-[#EF4444] transition-colors" aria-label="Remove alert"><Trash2 size={13} /></button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
